var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { AwsUi } from '../../../core/runtime/src';
import LegacyClassBuilder from '../../../core/utils/src/class-builders/legacy';
import { Button } from '../../button/src';
import * as BodyScroll from './BodyScroll';
var lastGeneratedId = 0;
var nextGeneratedId = function () { return "awsui-modal-" + lastGeneratedId++; };
var ESC = 27;
export var Modal = AwsUi.__addComponent('awsui-modal', {
    releaseStatus: 'stable',
    callbacks: {
        initialized: function () {
            if (!this.__dialogId) {
                this.__dialogId = nextGeneratedId();
            }
        },
        attached: function () {
            if (this.visible) {
                BodyScroll.disableBodyScrolling();
            }
        },
        detached: function () {
            // Ignoring multiple modal instance case for now
            BodyScroll.enableBodyScrolling();
            this.__initiallyFocusedElement = undefined;
        }
    },
    properties: {
        // Sets the width of the modal. `max` is variable width, set to the
        // largest size allowed by the design guidelines. Other sizes
        // (`small`/`medium`/`large`) have fixed widths.
        size: {
            type: 'string',
            valid: ['small', 'medium', 'large', 'max'],
            defaultValue: 'medium'
        },
        // If `true`, the modal will be displayed on the screen. Modals are hidden by default,
        // change this property to show them.
        visible: {
            type: 'boolean',
            onChange: function (wasVisible, isVisible) {
                var attached = this.__componentState === 'attached';
                var showing = attached && isVisible;
                if (showing) {
                    BodyScroll.disableBodyScrolling();
                }
                else {
                    BodyScroll.enableBodyScrolling();
                }
            }
        },
        // Expands the modal to fit its content. Instead of scrolling only the
        // body of the modal, the modal will grow to show all of its content, and
        // the scrollbar will be at page-level, instead of being inside the modal.
        // Set it to `true` if the modal contains elements with dropdowns (Select, Multiselect, Autosuggest, ButtonDropdown)
        expandToFit: {
            type: 'boolean'
        },
        // Adds aria label to the close button, in order to enhance
        // accessibility.
        closeLabel: {
            type: 'string'
        }
    },
    events: {
        // Fired when a user closes the modal (by clicking on the close icon,
        // by clicking outside of the modal or by pressing ESC).
        // The event details contain the `reason`, which can be any of the following:
        // `['closeButton', 'overlay', 'keyboard']`.
        dismiss: {
            bubbles: true,
            cancelable: true,
            detailType: 'Modal.DismissDetail'
        }
    },
    regions: {
        // Body of the modal.
        content: {
            isDefault: true
        },
        // Title of the modal. Even if this can be empty, it's suggested
        // that your modals always have a title.
        header: {},
        // Footer of the modal. If empty, the footer is not displayed at all.
        footer: {}
    },
    render: function (m, h) {
        var _this = this;
        var component = this;
        var dismiss = function (reason) {
            _this.__fireEvent('dismiss', { reason: reason }, {
                onNotCanceled: function () {
                    _this.visible = false;
                }
            });
        };
        function escKeyHandler(event) {
            if (event.keyCode === ESC) {
                dismiss('keyboard');
            }
        }
        var mousedownHandler = function (event) { return (_this.__mousedownTarget = event.target); };
        function overflowDismissHandler(event) {
            if (event.target === this) {
                // Some DOM utilities mocking the `click` event won't trigger a
                // `mousedown` before, so `__mousedownTarget` will be empty.
                if (component.__mousedownTarget === undefined || component.__mousedownTarget === this) {
                    dismiss('overlay');
                }
            }
        }
        function closeButtonHandler() {
            dismiss('closeButton');
        }
        var dialogClass = new LegacyClassBuilder(this)
            .addClass('awsui-modal-dialog')
            .addComponentClass('expandtofit', this.expandToFit)
            .addPropertyValueClass('size')
            .toClassName();
        var containerClass = new LegacyClassBuilder(this)
            .addClass('awsui-modal-container')
            .addComponentClass('expandtofit', this.expandToFit)
            .addComponentClass('hidden', !this.visible)
            .toClassName();
        var overlayClass = new LegacyClassBuilder(this)
            .addClass('awsui-modal-overlay')
            .addComponentClass('hidden', !this.visible)
            .toClassName();
        var tabTrap = function () { return (AwsUi.__createElement("div", { className: "awsui-modal-tabtrap", tabindex: _this.visible ? '0' : '-1', onfocus: function () { return setDialogFocus(_this); } })); };
        var bodyClass = new LegacyClassBuilder(this)
            .addComponentClass('body')
            .addComponentClass('expandtofit', this.expandToFit)
            .toClassName();
        var adjustScrollAndFocus = function (container) {
            if (_this.visible) {
                // Set focus on dialog to allow ESC key to dismiss
                setDialogFocus(_this);
                // When the user re-opens a modal, the scrollTop position remains the same
                // (because we reuse the element). We have to scroll back to top on open.
                var scrollableArea = _this.expandToFit ? container : container.querySelector('.awsui-modal-body');
                scrollableArea.scrollTop = 0;
            }
            else {
                // Restore focus to element that had it before dialog appeared
                restoreInitialFocus(_this);
            }
        };
        var attributes = {
            className: containerClass,
            role: 'dialog',
            'aria-modal': 'true',
            'aria-labelledby': this.__dialogId
        };
        var container = (AwsUi.__createElement("div", __assign({ lifecycleKey: "awsui-modal-focus" }, attributes, { onmousedown: mousedownHandler, onclick: overflowDismissHandler, componentDidMount: adjustScrollAndFocus, componentDidUpdate: adjustScrollAndFocus }),
            AwsUi.__createElement("div", { className: dialogClass, onkeydown: escKeyHandler, tabindex: "-1" },
                AwsUi.__createElement("div", { className: "awsui-modal-content awsui-util-container" },
                    AwsUi.__createElement("div", { className: "awsui-modal-header awsui-util-container-header" },
                        AwsUi.__createElement("div", { className: "awsui-modal-title", id: this.__dialogId }, h.region('header')),
                        AwsUi.__createElement(Button, { label: this.closeLabel, className: "awsui-modal-dismiss-control", variant: "icon", icon: "close", formAction: "none", click: closeButtonHandler })),
                    AwsUi.__createElement("div", { className: bodyClass }, h.region('content')),
                    h.region('footer', 'div', { className: 'awsui-modal-footer awsui-util-container-footer' })))));
        var overlay = AwsUi.__createElement("div", { className: overlayClass });
        return [tabTrap(), container, overlay, tabTrap()];
    }
});
function setDialogFocus(component) {
    var dialog = component.node.querySelector('.awsui-modal-dialog');
    if (document.activeElement === dialog || dialog.contains(document.activeElement)) {
        return;
    }
    if (dialog && dialog.focus) {
        component.__initiallyFocusedElement = document.activeElement;
        dialog.focus();
    }
    else {
        component.__initiallyFocusedElement = undefined;
    }
}
function restoreInitialFocus(component) {
    if (component.__initiallyFocusedElement && component.__initiallyFocusedElement.focus) {
        component.__initiallyFocusedElement.focus();
    }
    component.__initiallyFocusedElement = undefined;
}
//# sourceMappingURL=index.js.map